import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FormField, FormGrid, SelectionList, Text, } from '@dixa/lib-shared-habla';
import { useEffect, useMemo } from 'react';
import { Controller, useController, useForm, useWatch, } from 'react-hook-form';
import { HookFormSelectionList } from 'components/hookForm';
import { useBreakpointValue } from 'utils/mediaQuery';
import { AuthorizationHeader } from '../../../../../components/authorizationHeader/authorizationHeader';
import { CustomHeaders } from '../../../../../components/customHeaders/customHeaders';
import { OAuth } from '../../../../../components/oAuth/oAuth';
import { getEmptyInputMessage, isValidateCustomHeaders, thisFieldIsEmptyMessage, } from '../../../../../utils/validateMessages';
const authorizationMethods = {
    authorizationHeader: {
        id: 'authorizationHeader',
        label: 'Authorization header',
    },
    oAuth: { id: 'oAuth', label: 'OAuth' },
};
export const RequestHeaders = ({ control, className, selectedAuthorizationType, setSelectedAuthorizationType, disabled, handleCustomCardSubmit, }) => {
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const authorizationMethodSelectItems = useMemo(() => Object.keys(authorizationMethods).reduce((acc, key) => [
        ...acc,
        authorizationMethods[key],
    ], []), []);
    const { field: { onChange: onChangeOAuthStrategy }, } = useController({
        control,
        name: 'oAuthStrategy',
    });
    const authorizationMethodForm = useForm({
        defaultValues: { selectedAuthorizationMethod: [] },
    });
    const [selectedAuthorizationMethod] = useWatch({
        control: authorizationMethodForm.control,
        name: 'selectedAuthorizationMethod',
    });
    const [requestOAuthId, customCardAuthorizationHeader] = useWatch({
        control,
        name: [
            'request.o_auth_connection_id',
            'authorizationHeader',
        ],
    });
    // Setting the authorization method form if there is already an authorization
    // header or oAuth id in the custom card
    useEffect(() => {
        if (customCardAuthorizationHeader) {
            authorizationMethodForm.reset({
                selectedAuthorizationMethod: [authorizationMethods.authorizationHeader],
            });
        }
        else if (requestOAuthId) {
            authorizationMethodForm.reset({
                selectedAuthorizationMethod: [authorizationMethods.oAuth],
            });
        }
    }, [customCardAuthorizationHeader, requestOAuthId, authorizationMethodForm]);
    return (_jsxs(FormGrid, Object.assign({ className: className }, { children: [_jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 3 }, { children: _jsx(HookFormSelectionList, Object.assign({ label: "Authorization method", name: 'selectedAuthorizationMethod', control: authorizationMethodForm.control, disabled: disabled, componentProps: {
                        clearable: true,
                        placeholder: 'Select an authorization method',
                        getItemTitleByValue: (method) => (_jsx(Text.Paragraph, { children: method.label })),
                        equalityFn: (item1, item2) => item1.id === item2.id,
                    } }, { children: authorizationMethodSelectItems.map((method) => (_jsx(SelectionList.Item, Object.assign({ value: method }, { children: method.label }), method.id))) })) })), (selectedAuthorizationMethod === null || selectedAuthorizationMethod === void 0 ? void 0 : selectedAuthorizationMethod.id) === 'authorizationHeader' && (_jsx(_Fragment, { children: _jsx(FormField, { children: _jsx(Controller, { name: 'authorizationHeader', control: control, rules: {
                            required: {
                                value: selectedAuthorizationType !== 'none',
                                message: thisFieldIsEmptyMessage,
                            },
                            validate: (formValue) => {
                                var _a;
                                const bearerPrefix = 'bearer ';
                                if (selectedAuthorizationType !== 'none') {
                                    // We want to remove the automatically added bearer prefix
                                    // to see if the input field is actually empty
                                    return getEmptyInputMessage(((_a = formValue === null || formValue === void 0 ? void 0 : formValue.toLowerCase()) === null || _a === void 0 ? void 0 : _a.startsWith(bearerPrefix))
                                        ? formValue.slice(bearerPrefix.length)
                                        : formValue);
                                }
                                return undefined;
                            },
                        }, render: ({ field: { onChange }, fieldState: { error }, formState: { defaultValues }, }) => (_jsx(AuthorizationHeader, { disabled: disabled, value: defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.authorizationHeader, onChange: onChange, selectedAuthorizationType: selectedAuthorizationType, setSelectedAuthorizationType: setSelectedAuthorizationType, meta: {
                                error: error === null || error === void 0 ? void 0 : error.message,
                            } })) }) }) })), (selectedAuthorizationMethod === null || selectedAuthorizationMethod === void 0 ? void 0 : selectedAuthorizationMethod.id) === 'oAuth' && (_jsx(Controller, { name: 'request.o_auth_connection_id', control: control, rules: {
                    required: {
                        value: true,
                        message: 'Select a strategy, fill out the required fields and save the configuration',
                    },
                }, render: ({ field: { onChange }, fieldState: { error } }) => (_jsx(OAuth, { disabled: disabled, parentOAuthId: requestOAuthId, onChangeParentOAuthId: onChange, onChangeOAuthStrategy: onChangeOAuthStrategy, meta: {
                        error: error === null || error === void 0 ? void 0 : error.message,
                    }, handleParentSubmit: handleCustomCardSubmit })) })), _jsx(FormField, { children: _jsx(Controller, { name: 'customHeaders', control: control, rules: {
                        validate: (formValues) => isValidateCustomHeaders(formValues),
                    }, render: ({ field: { onChange }, formState: { defaultValues } }) => (_jsx(CustomHeaders, { disabled: disabled, onChange: onChange, value: defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.customHeaders })) }) })] })));
};
